import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeBracketSquareIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Text, Tooltip } from '../ui'
import {
  edgeFunctionsBackupService,
  formatEdgeFunctionUpdatedAt,
  isShowDirectoryPickerWriteAvailable,
  pickBackupDirectory,
  runEdgeFunctionsBackup,
} from '../../services/edgeFunctionsBackup'
import type {
  EdgeFunctionMeta,
  EdgeFunctionsBackupProgress,
} from '../../types/edgeFunctionsBackup'

const HELP_EDGE_FUNCTIONS = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Backup das Edge Functions</p>
    <p>
      Lista as Edge Functions publicadas no projeto (Management API) e grava o código-fonte numa
      pasta do computador (Chrome ou Edge), em edge-functions/&#123;slug&#125;/...
    </p>
    <p>
      A edge function edge-functions-backup precisa estar publicada e com o secret
      SB_ACCESS_TOKEN (permissão edge_functions_read).
    </p>
  </div>
)

const InfoTip: React.FC<{ content: React.ReactNode; label?: string }> = ({
  content,
  label = 'Saiba mais',
}) => (
  <Tooltip content={content} position="bottom" constrainWidth className="inline-flex shrink-0 align-middle">
    <button
      type="button"
      className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
      aria-label={label}
    >
      <InformationCircleIcon className="h-4 w-4" />
    </button>
  </Tooltip>
)

type Props = {
  reloadKey?: number
}

export const EdgeFunctionsBackup: React.FC<Props> = ({ reloadKey = 0 }) => {
  const [functions, setFunctions] = useState<EdgeFunctionMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sectionExpanded, setSectionExpanded] = useState(false)
  const [pickerAvailable] = useState(() => isShowDirectoryPickerWriteAvailable())

  const [backingUp, setBackingUp] = useState(false)
  const [progress, setProgress] = useState<EdgeFunctionsBackupProgress | null>(null)
  const [backupMessage, setBackupMessage] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const startLockRef = useRef(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await edgeFunctionsBackupService.list()
      setFunctions(data)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao listar Edge Functions'
      setError(msg)
      setFunctions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, reloadKey])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const cancelBackup = () => {
    abortRef.current?.abort()
  }

  const startBackup = async (slugs: string[]) => {
    if (!pickerAvailable || backingUp || startLockRef.current || slugs.length === 0) return
    startLockRef.current = true
    setSectionExpanded(true)

    // Trava a UI antes do seletor (evita double-click → "File picker already active")
    setBackingUp(true)
    setBackupMessage(null)
    setProgress({ done: 0, total: slugs.length, currentSlug: null, failed: [] })

    let rootDir
    try {
      rootDir = await pickBackupDirectory()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Não foi possível abrir o seletor de pasta'
      setBackupMessage(msg)
      setProgress(null)
      setBackingUp(false)
      startLockRef.current = false
      return
    }

    if (!rootDir) {
      setProgress(null)
      setBackingUp(false)
      startLockRef.current = false
      return
    }

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const result = await runEdgeFunctionsBackup({
        slugs,
        rootDir,
        signal: controller.signal,
        onProgress: setProgress,
      })

      const failCount = result.failed.length
      if (failCount === 0) {
        setBackupMessage(
          `Backup concluído: ${result.done} Edge Function(s) em edge-functions/.`,
        )
      } else {
        setBackupMessage(
          `Backup concluído com avisos: ${result.done - failCount} ok, ${failCount} falha(s).`,
        )
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        setBackupMessage('Backup cancelado.')
      } else {
        const msg = e instanceof Error ? e.message : 'Erro ao executar backup'
        setBackupMessage(msg)
      }
    } finally {
      abortRef.current = null
      setBackingUp(false)
      startLockRef.current = false
    }
  }

  const progressPct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((100 * progress.done) / progress.total))
      : 0

  return (
    <div className="px-6 py-6 border-t border-gray-200 dark:border-gray-700">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start min-w-0">
              <CodeBracketSquareIcon
                className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5"
                aria-hidden
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Backup das Edge Functions
                  </Heading>
                  <InfoTip content={HELP_EDGE_FUNCTIONS} label="Sobre o backup de Edge Functions" />
                </div>
                {!loading && !error && functions.length > 0 && (
                  <Text size="xs" color="muted" className="mt-1">
                    {functions.length} função(ões)
                  </Text>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {sectionExpanded && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pickerAvailable || backingUp || loading || functions.length === 0}
                  onClick={() => startBackup(functions.map((f) => f.slug))}
                  className="inline-flex items-center gap-1.5 text-sm"
                  title="Baixar todas as Edge Functions para uma pasta local"
                >
                  <ArrowDownTrayIcon className={`h-4 w-4 ${backingUp ? 'animate-pulse' : ''}`} />
                  Backup de todas
                </Button>
              )}
              <button
                type="button"
                onClick={() => setSectionExpanded((v) => !v)}
                className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
              >
                {sectionExpanded ? (
                  <>
                    <ChevronUpIcon className="h-5 w-5" />
                    Ocultar
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-5 w-5" />
                    Ver{!loading && functions.length > 0 ? ` (${functions.length})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {sectionExpanded && (
          <div className="px-6 py-4 space-y-4">
            {!pickerAvailable && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                <Text size="sm" className="text-amber-800 dark:text-amber-200">
                  O backup local exige Chrome ou Edge (seletor de pasta com permissão de escrita). Neste
                  navegador os botões de backup ficam desabilitados.
                </Text>
              </div>
            )}

            {backingUp && progress && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                    Backup em andamento: {progress.done} / {progress.total}
                  </Text>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelBackup}
                    className="inline-flex items-center gap-1 text-xs"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Cancelar
                  </Button>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {progress.currentSlug && (
                  <Text size="xs" color="muted" className="mt-2 font-mono truncate">
                    {progress.currentSlug}
                  </Text>
                )}
              </div>
            )}

            {backupMessage && !backingUp && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                <Text size="sm" className="text-gray-800 dark:text-gray-200">
                  {backupMessage}
                </Text>
                {progress && progress.failed.length > 0 && (
                  <ul className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {progress.failed.slice(0, 20).map((f) => (
                      <li key={f.slug}>
                        <Text size="xs" className="font-mono text-red-700 dark:text-red-300">
                          {f.slug}: {f.error}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {loading ? (
              <div className="p-6 animate-pulse h-24 rounded-lg bg-gray-100 dark:bg-gray-700" />
            ) : error ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                  {error}
                </Text>
                <Text size="xs" color="muted" className="mt-2">
                  Publique a função edge-functions-backup e configure o secret SB_ACCESS_TOKEN
                  (veja supabase/functions/edge-functions-backup/README.md).
                </Text>
              </div>
            ) : functions.length === 0 ? (
              <div className="px-2 py-6 text-center">
                <Text size="sm" color="muted">
                  Nenhuma Edge Function encontrada no projeto.
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Versão
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Atualizado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {functions.map((fn) => (
                      <tr key={fn.slug} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Text size="sm" className="text-gray-900 dark:text-white font-mono font-semibold">
                            {fn.slug}
                          </Text>
                          {fn.verify_jwt === false && (
                            <Text size="xs" color="muted" className="mt-0.5">
                              JWT desativado
                            </Text>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={
                              String(fn.status).toUpperCase() === 'ACTIVE'
                                ? 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                : 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }
                          >
                            {fn.status || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Text size="sm" className="text-gray-900 dark:text-white">
                            {fn.version != null ? fn.version : '—'}
                          </Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300">
                            {formatEdgeFunctionUpdatedAt(fn.updated_at)}
                          </Text>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!pickerAvailable || backingUp}
                            onClick={() => startBackup([fn.slug])}
                            className="inline-flex items-center gap-1.5 text-xs"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Backup
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default EdgeFunctionsBackup
